import { Injectable } from '@angular/core';
import {
    HttpRequest,
    HttpHandler,
    HttpInterceptor,
    HttpErrorResponse,
    HttpResponse,
    HttpClient,
} from '@angular/common/http';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { catchError, finalize, tap } from 'rxjs/operators';
import { throwError } from 'rxjs';
import { Location } from '@angular/common';

import { SystemMessageService } from '../system-message/system-message-service';
import { SpinnerService } from '../layout/spinner/spinner.service';
import { SecurityService } from '../security/security.service';

@Injectable()
export class HttpClientInterceptor implements HttpInterceptor {
    reqCount = 0;

    constructor(
        private ms: SystemMessageService,
        private spinner: SpinnerService,
        private location: Location,
        private sec: SecurityService,
        private http: HttpClient,
        private router: Router,
        private modal: NgbModal
    ) {}

    intercept(req: HttpRequest<any>, next: HttpHandler) {
        ++this.reqCount;
        this.setSpinner();

        // Get the URL, with automatic base URL, if beginning with 'api/' or '/api/'
        let url =
            req.url.indexOf('api/') === 0 || req.url.indexOf('/api/') === 0
                ? document.getElementsByTagName('base')[0].href + req.url
                : req.url;
        url = url.replace('//api', '/api');

        // Add the UTC offset as a query string
        const timeZone =
            typeof Intl.DateTimeFormat().resolvedOptions !== 'undefined'
                ? Intl.DateTimeFormat().resolvedOptions().timeZone
                : undefined;
        url =
            url +
            (url.includes('?') ? '&' : '?') +
            `userTimeZone=${encodeURIComponent(timeZone)}&` +
            `utcOffset=${new Date().getTimezoneOffset() / -60}`;

        // Set the default content type and URL
        let newReq =
            req.headers['Content-Type'] || req.body instanceof FormData
                ? req.clone({ url })
                : req.clone({
                      headers: req.headers.set(
                          'Content-Type',
                          'application/json'
                      ),
                      url,
                  });

        // Get the auth token from the service.
        const authToken = localStorage.getItem('jwt')
            ? JSON.parse(localStorage.getItem('jwt'))
            : null;

        // Add the auth token to the header, if we have one
        if (authToken) {
            newReq = newReq.clone({
                headers: newReq.headers.set(
                    'Authorization',
                    'Bearer ' + authToken
                ),
            });
        }

        // Set new last access date
        this.sec.setLocalStorage('lastAccess', new Date());

        // Send cloned request with header to the next handler.
        return next.handle(newReq).pipe(
            tap((event) => {
                // The server re-issues the token on activity (sliding
                // expiration), so store the new one when it comes back
                if (event instanceof HttpResponse) {
                    const refreshedToken =
                        event.headers.get('X-Refreshed-Token');
                    if (refreshedToken) {
                        this.sec.setSecurity(
                            refreshedToken,
                            undefined,
                            undefined
                        );
                    }
                }
            }),
            catchError(this.handleError.bind(this)),
            finalize(() => {
                --this.reqCount;
                this.setSpinner();
            })
        );
    }

    /**
     * This is our default error handling function throughout the application.
     * @param err The error response.
     */
    handleError(err: HttpErrorResponse) {
        switch (err.status) {
            case 401:
                this.modal.dismissAll(); // Make sure all modals are closed since they are moving to the login screen
                this.sec.setSecurity(null, null, null); // Clear out the user security
                const redirectUrl =
                    '/' +
                    window.location.href.replace(
                        document.getElementsByTagName('base')[0].href,
                        ''
                    );
                this.router.navigateByUrl(
                    `/login?redirectUrl=${encodeURIComponent(redirectUrl)}`
                );
                break;
            case 403:
                this.ms.setSystemMessage(err.error, 'error');
                this.http
                    .get('api/Account/GetUserInfo')
                    .subscribe((model: any) => {
                        this.sec.setSecurity(
                            this.sec.getToken(),
                            model.user,
                            model.security
                        );
                        this.router.navigateByUrl('/');
                    });
                break;
            case 466: // This can sometimes be used to indicate confirmation messages, so ignore here
                break;
            case 500:
                this.ms.setSystemMessage(
                    'We are aware of the current issue and are working hard on getting it fixed as soon as possible. ' +
                        'If this message persists for too long, please contact us. Sorry for the inconvenience.',
                    'error'
                );
                break;
            default:
                this.ms.setSystemMessage(
                    'The request could not be processed because a network transmission error occurred.',
                    'error'
                );
        }
        return throwError(err);
    }

    /**
     * Show and hide the spinner, depending on whether or not a request
     * is still out.
     */
    setSpinner() {
        if (this.reqCount === 0) {
            this.spinner.hideSpinner();
        } else {
            this.spinner.showSpinner();
        }
    }
}
